import "./UserManagement.less";

import {
	notification,
	Button,
	Popconfirm,
	Table,
} from "antd";
import React from "react";

import { CancellationToken } from "lib/api/base";
import { UserApi } from "lib/api/security/user";
import { IBaseProps } from "lib/data/interfaces";
import { User } from "lib/data/model";

import { ColumnProps } from "antd/lib/table";
import { TransitionRoute } from "components/general/TransitionRoute";
import { StateService, UtilityService } from "lib/services";
import { cloneDeep } from "lodash";
import { curry, __ } from "lodash/fp";
import { RegistrationSection } from "./RegistrationSection";
import Search from "antd/lib/input/Search";


interface IUserManagementProps extends IBaseProps {
}

interface IUserManagementState {
	registeringAccount: boolean;
	sectionVisible: "userTable" | "newUser";
	loadingUserTable: boolean;
	currentUser: User;
	users: User[];
	filterValue: string;
}


export class UserManagement extends React.Component<IUserManagementProps, IUserManagementState> {

	private apiCancellationToken: CancellationToken = new CancellationToken();


	constructor(props: IUserManagementProps) {
		super(props);

		this.state = {
			registeringAccount: false,
			sectionVisible: "userTable",
			currentUser: new User(),
			loadingUserTable: false,
			users: [],
			filterValue: ""
		};
	}

	public componentDidMount = async () => {
		this.getUsers();
	}

	public componentWillUnmount() {

		// Cancel Outstanding API Calls
		this.apiCancellationToken.cancel();
	}

	public getUsers = async () => {

		StateService.setCancelableState(() => this.setState({ loadingUserTable: true }), this.apiCancellationToken);

		const users = await UtilityService.asyncTimeout(UserApi.getAllUsers(), 700);

		StateService.setCancelableState(() => this.setState({ users, loadingUserTable: false }), this.apiCancellationToken);
	}

	public createUser = async (_user: User): Promise<void> => {

		// retrieve the users, and display the table
		this.getUsers();

		StateService.setCancelableState(() => this.setState({
			registeringAccount: false,
			sectionVisible: "userTable",
			currentUser: new User()
		}), this.apiCancellationToken);
	}

	public render() {

		const columns: Array<ColumnProps<User>> = [
			{
				title: "Username",
				dataIndex: "username",
				key: "username"
			},
			{
				title: "Email",
				dataIndex: "email",
				key: "email"
			},
			{
				title: "First Name",
				dataIndex: "firstName",
				key: "firstName"
			},
			{
				title: "Last Name",
				dataIndex: "lastName",
				key: "lastName"
			},
			{
				title: "Id",
				dataIndex: "id",
				key: "id"
			},
			{
				align: "center",
				width: 100,
				render: (_label: string, userRecord: User) => {

					return (
						<Button.Group>
							<Button
								key={`edit-user-button-${userRecord.id}`}
								onClick={curry(this.onEditUser)(__, cloneDeep(userRecord))}
								icon="edit"
							/>
							<Popconfirm
								title="Are you sure delete this user?"
								onConfirm={curry(this.onDeleteUser)(__, userRecord)}
								okText="Yes"
								cancelText="No"
							>
								<Button
									key={`delete-user-button-${userRecord.id}`}
									icon="delete"
									type="danger"
								/>
							</Popconfirm>
						</Button.Group>
					);
				}
			}
		];

		const filteredUsers = this.state.users.filter((user) => {

			const filterValue = this.state.filterValue.trim().toLocaleLowerCase();

			if (filterValue === "") {

				return true;
			}

			return user.username.toLocaleLowerCase().includes(filterValue) ||
					user.firstName.toLocaleLowerCase().includes(filterValue) ||
					user.lastName.toLocaleLowerCase().includes(filterValue) ||
					user.email.toLocaleLowerCase().includes(filterValue) ||
					`${user.firstName} ${user.lastName}`.toLocaleLowerCase().includes(filterValue) ||
					`${user.lastName} ${user.firstName}`.toLocaleLowerCase().includes(filterValue);
		});

		return (

			<div className="user-managment">

				<TransitionRoute on={this.state.sectionVisible} case="newUser">

					<RegistrationSection
						onFormCancel={() => StateService.setCancelableState(() => this.setState({ sectionVisible: "userTable", currentUser: new User() }), this.apiCancellationToken)}
						user={this.state.currentUser}
						onSaveSuccess={this.createUser}
					/>

				</TransitionRoute>

				<TransitionRoute on={this.state.sectionVisible} case="userTable">

					<div className="user-table-container">

						<div className="search-bar-container">
							<Search
								className="search-bar"
								placeholder="Search Users"
								onSearch={(value) => this.setState({filterValue: value})}
								onChange={(event) => this.setState({filterValue: event.target.value})}
							/>
						</div>

						<Table
							className="user-management-table"
							loading={this.state.loadingUserTable}
							dataSource={filteredUsers}
							rowKey={(record) => record.id!.toString()}
							columns={columns}
							footer={this.getAddUserButton}
							pagination={{ style: { padding: "0px 10px" } }}
						/>
					</div>

				</TransitionRoute>
			</div>
		);
	}

	private getAddUserButton = () => (
		<Button
			type="primary"
			icon="user-add"
			onClick={() => StateService.setCancelableState(() => this.setState({ sectionVisible: "newUser" }), this.apiCancellationToken)}
		>
			Create New User
		</Button>
	)

	private onEditUser = (_event: React.MouseEvent, user: User) => {

		StateService.setCancelableState(() => this.setState({
			currentUser: user,
			sectionVisible: "newUser"
		}), this.apiCancellationToken);
	}

	private onDeleteUser = async (_event: React.MouseEvent, user: User) => {

		if (user.id) {

			await UserApi.deleteUser(user.id);
			this.getUsers();
		}
	}
}
